import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import api from '../../services/api';
import { revalidatePublicPath } from '../../utils/revalidatePublic';

const PUBLIC_PATHS = { es: '/es/descargar-app', en: '/en/download-app' };
const CLOUD_NAME = 'dtxar5b36';
const UPLOAD_PRESET = 'bordergarita_uploads';
const UPLOAD_FOLDER = 'download_app';

const SIDE_OPTIONS = [
  { label: 'Imagen a la derecha', value: 'right' },
  { label: 'Imagen a la izquierda', value: 'left' },
];

const BACKGROUND_OPTIONS = [
  { label: 'Blanco', value: 'white' },
  { label: 'Verde', value: 'green' },
  { label: 'Gris', value: 'muted' },
];

async function uploadImage(file) {
  const form = new FormData();
  form.append('file', file);
  form.append('upload_preset', UPLOAD_PRESET);
  form.append('folder', UPLOAD_FOLDER);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
    method: 'POST',
    body: form,
  });
  const json = await res.json();
  if (!res.ok || !json.secure_url) {
    throw new Error(json.error?.message || 'No se pudo subir la imagen');
  }
  return json.secure_url;
}

function blankText(index) {
  return {
    id: `body${index + 1}`,
    eyebrow: '',
    title: '',
    body: '',
    image_alt: '',
  };
}

function blankVisual(index) {
  return {
    image_url: '',
    image_side: index % 2 === 0 ? 'right' : 'left',
    background: index % 2 === 0 ? 'white' : 'green',
    enabled: true,
  };
}

function Field({ label, value, onChange, textarea, hint }) {
  return (
    <label className="flex flex-column gap-1 mb-3">
      <span className="text-sm font-semibold">{label}</span>
      {textarea ? (
        <InputTextarea
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          rows={4}
          autoResize
        />
      ) : (
        <InputText value={value || ''} onChange={(event) => onChange(event.target.value)} />
      )}
      {hint ? <small className="text-color-secondary">{hint}</small> : null}
    </label>
  );
}

function ImageSlot({ url, alt, uploading, onPick, onClear, label }) {
  return (
    <div className="flex flex-column gap-2">
      <span className="text-sm font-semibold">{label}</span>
      {url ? (
        <img
          src={url}
          alt={alt || ''}
          style={{ width: '100%', maxHeight: 220, objectFit: 'contain', background: '#f3f4f6', borderRadius: 8 }}
        />
      ) : (
        <div
          style={{
            height: 140,
            borderRadius: 8,
            background: '#e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b7280',
            fontSize: 13,
          }}
        >
          Sin imagen
        </div>
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          label={uploading ? 'Subiendo…' : url ? 'Cambiar imagen' : 'Subir imagen'}
          icon="pi pi-upload"
          className="p-button-sm p-button-outlined"
          loading={uploading}
          onClick={onPick}
        />
        {url ? (
          <Button
            type="button"
            label="Quitar"
            icon="pi pi-times"
            className="p-button-sm p-button-text p-button-danger"
            disabled={uploading}
            onClick={onClear}
          />
        ) : null}
      </div>
    </div>
  );
}

export default function DownloadAppManager() {
  const toast = useRef(null);
  const fileRef = useRef(null);
  const pickTarget = useRef(null);
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState('');
  const [error, setError] = useState('');

  const publicUrl = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL || '';
  const publicLinks = [
    { label: 'Español', href: `${publicUrl}${PUBLIC_PATHS.es}` },
    { label: 'English', href: `${publicUrl}${PUBLIC_PATHS.en}` },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/download_app');
      setKit(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'No se pudo cargar la landing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (recipe) => {
    setKit((prev) => {
      const next = structuredClone(prev);
      recipe(next);
      return next;
    });
  };

  const requestUpload = (target) => {
    pickTarget.current = target;
    fileRef.current?.click();
  };

  const onFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const target = pickTarget.current;
    if (!file || !target) return;
    const key = `${target.kind}-${target.index}`;
    setUploading(key);
    try {
      const url = await uploadImage(file);
      patch((next) => {
        if (target.kind === 'hero') next.shared.hero_images[target.index].url = url;
        if (target.kind === 'section') next.shared.sections[target.index].image_url = url;
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Imagen',
        detail: err.message || 'No se pudo subir',
        life: 4000,
      });
    } finally {
      setUploading('');
    }
  };

  const addSection = () => {
    patch((next) => {
      const index = next.shared.sections.length;
      const id = `body${index + 1}`;
      next.shared.sections.push(blankVisual(index));
      next.es.sections.push({ ...blankText(index), id });
      next.en.sections.push({ ...blankText(index), id });
    });
  };

  const removeSection = (index) => {
    const label = kit?.es?.sections?.[index]?.id || `body${index + 1}`;
    if (!window.confirm(`¿Quitar la sección ${label}? Se elimina en español e inglés.`)) return;
    patch((next) => {
      next.shared.sections.splice(index, 1);
      next.es.sections.splice(index, 1);
      next.en.sections.splice(index, 1);
    });
  };

  const moveSection = (index, direction) => {
    const target = index + direction;
    patch((next) => {
      if (target < 0 || target >= next.shared.sections.length) return;
      const swap = (list) => {
        const [item] = list.splice(index, 1);
        list.splice(target, 0, item);
      };
      swap(next.shared.sections);
      swap(next.es.sections);
      swap(next.en.sections);
    });
  };

  const persist = async () => {
    const res = await api.put('/download_app', {
      shared: kit.shared,
      es: kit.es,
      en: kit.en,
    });
    setKit(res.data);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await persist();
      toast.current?.show({ severity: 'success', summary: 'Guardado', life: 2500 });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'No se guardó',
        detail: err?.response?.data?.error || err.message,
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const onPublish = async () => {
    const secret = process.env.REACT_APP_BORDERGARITA_REVALIDATE_SECRET || '';
    if (!publicUrl || !secret) {
      toast.current?.show({
        severity: 'error',
        summary: 'Publicar',
        detail: 'Falta REACT_APP_BORDERGARITA_PUBLIC_APP_URL o REVALIDATE_SECRET',
        life: 5000,
      });
      return;
    }
    setPublishing(true);
    try {
      await persist();
      await Promise.all(
        [PUBLIC_PATHS.es, PUBLIC_PATHS.en].map((path) => revalidatePublicPath(publicUrl, secret, path))
      );
      toast.current?.show({ severity: 'success', summary: 'Publicado', life: 2500 });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'No se publicó',
        detail: err?.response?.data?.error || err.message,
        life: 5000,
      });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="p-3" style={{ paddingBottom: 120 }}>
      <Toast ref={toast} />
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={onFile}
      />
      <div className="mb-3">
        <h2 className="m-0">Descargar app</h2>
        <p className="mt-2 mb-0 text-color-secondary" style={{ maxWidth: 760 }}>
          Landing de <strong>/es/descargar-app</strong> y <strong>/en/download-app</strong>.
          El header es uno solo. Las secciones body se agregan, se reordenan y cada una lleva su imagen.
          Si dejas vacía la URL de una tienda, esa tienda muestra el aviso de que llega pronto.
          En los textos, <strong>**así**</strong> sale en negrita.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        {publicLinks.map((link) => (
          <a
            key={link.label}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              flex: '1 1 280px',
              padding: '12px 16px',
              border: '1px solid #e4e4ed',
              borderRadius: 12,
              background: '#fff',
              textDecoration: 'none',
              color: '#1a1a2e',
            }}
          >
            <strong>{link.label}</strong>
            <span style={{ color: '#2020a0', fontSize: 13, wordBreak: 'break-all' }}>{link.href}</span>
          </a>
        ))}
      </div>

      {error ? <Message severity="error" className="mb-3 w-full" text={error} /> : null}
      {loading ? <p>Cargando landing…</p> : null}

      {!loading && kit ? (
        <>
          <section className="mb-4 p-3 border-round" style={{ border: '1px solid #e4e4ed', background: '#fff' }}>
            <h3 className="mt-0">Tiendas</h3>
            <p className="text-color-secondary mt-0">
              Estas URLs son las mismas en los dos idiomas. Si una queda vacía, no se muestra el botón de esa tienda.
            </p>
            <div className="flex flex-wrap gap-3">
              <div style={{ flex: '1 1 320px' }}>
                <Field
                  label="Google Play"
                  value={kit.shared.google_play_url}
                  hint="Vacío = “Muy pronto en Google Play”"
                  onChange={(value) => patch((next) => { next.shared.google_play_url = value; })}
                />
              </div>
              <div style={{ flex: '1 1 320px' }}>
                <Field
                  label="App Store"
                  value={kit.shared.app_store_url}
                  hint="Vacío = “Muy pronto en el App Store”"
                  onChange={(value) => patch((next) => { next.shared.app_store_url = value; })}
                />
              </div>
            </div>
          </section>

          <section className="mb-4 p-3 border-round" style={{ border: '1px solid #e4e4ed', background: '#fff' }}>
            <h3 className="mt-0">Header</h3>
            <p className="text-color-secondary mt-0">Solo hay un header. Aquí va el título, el texto y las frases de “próximamente”.</p>
            <div className="flex flex-wrap gap-4">
              {['es', 'en'].map((lang) => (
                <div key={lang} style={{ flex: '1 1 360px', minWidth: 0 }}>
                  <h4 className="mt-0">{lang === 'es' ? 'Español' : 'English'}</h4>
                  <Field label="SEO title" value={kit[lang].seo.title} onChange={(value) => patch((next) => { next[lang].seo.title = value; })} />
                  <Field label="SEO description" textarea value={kit[lang].seo.description} onChange={(value) => patch((next) => { next[lang].seo.description = value; })} />
                  <Field label="Antetítulo" value={kit[lang].header.eyebrow} onChange={(value) => patch((next) => { next[lang].header.eyebrow = value; })} />
                  <Field label="Título, antes del destacado" value={kit[lang].header.title_lead} onChange={(value) => patch((next) => { next[lang].header.title_lead = value; })} />
                  <Field label="Título destacado (verde)" value={kit[lang].header.title_highlight} onChange={(value) => patch((next) => { next[lang].header.title_highlight = value; })} />
                  <Field label="Título, después del destacado" value={kit[lang].header.title_tail} onChange={(value) => patch((next) => { next[lang].header.title_tail = value; })} />
                  <Field label="Descripción" textarea value={kit[lang].header.description} onChange={(value) => patch((next) => { next[lang].header.description = value; })} />
                  <Field label="Texto pequeño de Google Play" value={kit[lang].header.play_kicker} onChange={(value) => patch((next) => { next[lang].header.play_kicker = value; })} />
                  <Field label="Texto pequeño de App Store" value={kit[lang].header.apple_kicker} onChange={(value) => patch((next) => { next[lang].header.apple_kicker = value; })} />
                  <Field label="Nombre de App Store" value={kit[lang].header.apple_label} onChange={(value) => patch((next) => { next[lang].header.apple_label = value; })} />
                  <Field label="Aviso si no hay Google Play" value={kit[lang].header.play_soon} onChange={(value) => patch((next) => { next[lang].header.play_soon = value; })} />
                  <Field label="Aviso si no hay App Store" value={kit[lang].header.apple_soon} onChange={(value) => patch((next) => { next[lang].header.apple_soon = value; })} />
                  <Field
                    label="Aviso si no hay ninguna tienda"
                    textarea
                    value={kit[lang].header.both_soon}
                    onChange={(value) => patch((next) => { next[lang].header.both_soon = value; })}
                  />
                </div>
              ))}
            </div>
            <h4>Celulares del header</h4>
            <div className="flex flex-wrap gap-4">
              {kit.shared.hero_images.map((image, index) => (
                <div key={index} style={{ flex: '1 1 240px', maxWidth: 320 }}>
                  <ImageSlot
                    label={index === 0 ? 'Celular grande' : 'Celular chico'}
                    url={image.url}
                    uploading={uploading === `hero-${index}`}
                    onPick={() => requestUpload({ kind: 'hero', index })}
                    onClear={() => patch((next) => { next.shared.hero_images[index].url = ''; })}
                  />
                </div>
              ))}
            </div>
          </section>

          <section className="mb-4">
            <div className="flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
              <div>
                <h3 className="m-0">Secciones body</h3>
                <p className="text-color-secondary mt-2 mb-0">
                  Cada bloque es un body. La imagen, el lado y el fondo se comparten; el texto cambia por idioma.
                </p>
              </div>
              <Button type="button" label="Agregar sección" icon="pi pi-plus" onClick={addSection} />
            </div>
            {kit.shared.sections.map((visual, index) => {
              const es = kit.es.sections[index] || blankText(index);
              const en = kit.en.sections[index] || blankText(index);
              return (
                <article
                  key={`${es.id}-${index}`}
                  className="mb-3 p-3 border-round"
                  style={{ border: '1px solid #e4e4ed', background: '#fff' }}
                >
                  <div className="flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
                    <h4 className="m-0">{es.id || `body${index + 1}`}</h4>
                    <div className="flex align-items-center gap-2 flex-wrap">
                      <span className="text-sm">Visible</span>
                      <InputSwitch
                        checked={visual.enabled !== false}
                        onChange={(event) => patch((next) => { next.shared.sections[index].enabled = event.value; })}
                      />
                      <Button type="button" icon="pi pi-arrow-up" className="p-button-text p-button-sm" disabled={index === 0} onClick={() => moveSection(index, -1)} />
                      <Button type="button" icon="pi pi-arrow-down" className="p-button-text p-button-sm" disabled={index === kit.shared.sections.length - 1} onClick={() => moveSection(index, 1)} />
                      <Button type="button" icon="pi pi-trash" className="p-button-text p-button-danger p-button-sm" onClick={() => removeSection(index)} />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 mb-3">
                    <div style={{ flex: '1 1 220px' }}>
                      <span className="text-sm font-semibold block mb-2">Identificador</span>
                      <InputText
                        value={es.id || ''}
                        onChange={(event) => {
                          const value = event.target.value;
                          patch((next) => {
                            next.es.sections[index].id = value;
                            next.en.sections[index].id = value;
                          });
                        }}
                      />
                    </div>
                    <div style={{ flex: '1 1 220px' }}>
                      <span className="text-sm font-semibold block mb-2">Lado de la imagen</span>
                      <Dropdown
                        value={visual.image_side}
                        options={SIDE_OPTIONS}
                        onChange={(event) => patch((next) => { next.shared.sections[index].image_side = event.value; })}
                        className="w-full"
                      />
                    </div>
                    <div style={{ flex: '1 1 220px' }}>
                      <span className="text-sm font-semibold block mb-2">Fondo</span>
                      <Dropdown
                        value={visual.background}
                        options={BACKGROUND_OPTIONS}
                        onChange={(event) => patch((next) => { next.shared.sections[index].background = event.value; })}
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div className="mb-3" style={{ maxWidth: 420 }}>
                    <ImageSlot
                      label="Imagen de la sección"
                      url={visual.image_url}
                      alt={es.image_alt}
                      uploading={uploading === `section-${index}`}
                      onPick={() => requestUpload({ kind: 'section', index })}
                      onClear={() => patch((next) => { next.shared.sections[index].image_url = ''; })}
                    />
                  </div>
                  <div className="flex flex-wrap gap-4">
                    {[['es', es], ['en', en]].map(([lang, section]) => (
                      <div key={lang} style={{ flex: '1 1 320px', minWidth: 0 }}>
                        <h5 className="mt-0">{lang === 'es' ? 'Español' : 'English'}</h5>
                        <Field label="Antetítulo" value={section.eyebrow} onChange={(value) => patch((next) => { next[lang].sections[index].eyebrow = value; })} />
                        <Field label="Título" textarea value={section.title} onChange={(value) => patch((next) => { next[lang].sections[index].title = value; })} />
                        <Field
                          label="Texto"
                          textarea
                          hint="**negrita**"
                          value={section.body}
                          onChange={(value) => patch((next) => { next[lang].sections[index].body = value; })}
                        />
                        <Field label="Texto alterno de la imagen" value={section.image_alt} onChange={(value) => patch((next) => { next[lang].sections[index].image_alt = value; })} />
                      </div>
                    ))}
                  </div>
                </article>
              );
            })}
          </section>

          <section className="mb-4 p-3 border-round" style={{ border: '1px solid #e4e4ed', background: '#fff' }}>
            <h3 className="mt-0">Cierre</h3>
            <p className="text-color-secondary mt-0">
              El botón baja a las tiendas. Si solo hay una URL, abre esa tienda. Si no hay ninguna, lleva al aviso de próximamente.
            </p>
            <div className="mb-3" style={{ maxWidth: 280 }}>
              <span className="text-sm font-semibold block mb-2">Fondo</span>
              <Dropdown
                value={kit.shared.closing_background}
                options={BACKGROUND_OPTIONS}
                onChange={(event) => patch((next) => { next.shared.closing_background = event.value; })}
                className="w-full"
              />
            </div>
            <div className="flex flex-wrap gap-4">
              {['es', 'en'].map((lang) => (
                <div key={lang} style={{ flex: '1 1 320px', minWidth: 0 }}>
                  <h4 className="mt-0">{lang === 'es' ? 'Español' : 'English'}</h4>
                  <Field label="Antetítulo" value={kit[lang].closing.eyebrow} onChange={(value) => patch((next) => { next[lang].closing.eyebrow = value; })} />
                  <Field label="Título" textarea value={kit[lang].closing.title} onChange={(value) => patch((next) => { next[lang].closing.title = value; })} />
                  <Field label="Texto" textarea hint="**negrita**" value={kit[lang].closing.body} onChange={(value) => patch((next) => { next[lang].closing.body = value; })} />
                  <Field label="Botón" value={kit[lang].closing.button_label} onChange={(value) => patch((next) => { next[lang].closing.button_label = value; })} />
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}

      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '720px',
          maxWidth: '95vw',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '1rem',
          backgroundColor: 'white',
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
        }}
      >
        <Button
          type="button"
          label="Publicar"
          icon="pi pi-cloud-upload"
          className="p-button-warning"
          loading={publishing}
          disabled={!kit || loading || saving}
          onClick={onPublish}
          style={{ width: '200px' }}
        />
        <Button
          type="button"
          label="Guardar"
          icon="pi pi-save"
          className="main-button"
          loading={saving}
          disabled={!kit || loading || publishing}
          onClick={onSave}
          style={{ width: '200px' }}
        />
      </div>
    </div>
  );
}
